/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import * as React from 'react'
import { Button, Icon, Tooltip } from '@components/atoms'
import { mono, boFont, boSize } from '@components/systems/backoffice/backoffice.tokens'

import { ButtonPropsColorOverrides } from '@mui/material'
import { OverridableStringUnion } from '@mui/types'

interface PropsType {
	title?: string
	className?: string
	style?: React.CSSProperties
	tooltip?: string
	buttonColor?: OverridableStringUnion<
		'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning',
		ButtonPropsColorOverrides
	>
	onClick?: () => void
	iconName?: string
	/** 유지용 — Monolith 에서는 아이콘 색을 따로 칠하지 않는다(무시). 색 대신 variant 가 위계를 정한다 */
	iconTextColor?: string
	disabled?: boolean
	variant?: 'contained' | 'outlined' | 'text'
	shouldPreventDuplicatedClick?: boolean
}

/**
 * 아이콘 + 라벨 버튼. 세 단계뿐이다:
 * contained = 블랙(화면당 하나) · outlined = 고스트 · text = 텍스트(dim → hover ink).
 * `buttonColor` 는 API 유지용으로 받기만 한다 — 위계는 색이 아니라 variant 로 갈린다.
 */
export const ActionButton = (props: PropsType) => {
	const variant = props.variant ?? 'text'
	// contained + inherit 은 예전 코드에서 "회색 채움"이었다 → 고스트로 내린다
	const resolvedVariant: 'contained' | 'outlined' | 'text' =
		variant === 'contained' && (props.buttonColor ?? 'inherit') === 'inherit'
			? 'outlined'
			: variant

	return (
		<Tooltip title={props.tooltip ?? props.title ?? ''}>
			<span className="actionButtonWrap">
				<Button
					className={`actionButton ${resolvedVariant} ${props.className ?? ''}`}
					css={[style, { ...props.style }]}
					size="small"
					color="primary"
					onClick={props.onClick}
					shouldPreventDuplicatedClick={props.shouldPreventDuplicatedClick}
					disabled={props.disabled ?? false}
					variant={resolvedVariant}
				>
					<Icon name={props.iconName ?? 'check'} className="actionButtonIcon" />
					{props.title !== undefined && props.title !== '' ? (
						<span className="actionButtonTitle">{props.title}</span>
					) : null}
				</Button>
			</span>
		</Tooltip>
	)
}

const style = css({
	height: boSize.ctlH,
	minHeight: boSize.ctlH,
	minWidth: 0,
	margin: 0,
	padding: '0 12px',
	gap: 6,
	fontFamily: boFont.ui,
	fontSize: 13,
	fontWeight: 500,
	lineHeight: 1,
	borderRadius: 2,
	boxShadow: 'none',
	textTransform: 'none',
	whiteSpace: 'nowrap',
	transition:
		'color 200ms ease-out, background-color 200ms ease-out, border-color 200ms ease-out',

	'.actionButtonIcon': {
		fontSize: 17,
		lineHeight: 1,
		color: 'inherit',
	},

	'.actionButtonTitle': {
		margin: 0,
		padding: 0,
	},

	'&.contained': {
		background: mono.ctaBg,
		color: '#FFFFFF',
		'&:hover': { background: mono.ink, boxShadow: 'none' },
	},
	'&.outlined': {
		background: 'transparent',
		color: mono.ink,
		border: `1px solid ${mono.ink}`,
		'&:hover': { background: mono.ink, color: '#FFFFFF' },
	},
	'&.text': {
		padding: '0 8px',
		background: 'transparent',
		color: mono.dim,
		'&:hover': { color: mono.ink, background: 'transparent' },
	},

	'&.Mui-disabled': {
		opacity: 0.35,
		color: 'inherit',
		borderColor: 'currentColor',
	},
	'&.contained.Mui-disabled': { background: mono.ctaBg, color: '#FFFFFF' },
})
